// Getting the browser's own MediaStream out of a LiveSwitch media object.
//
// This exists because `getView()` is not the route it looks like it is.
// Reading the SDK's source settles it:
//
//   * Video renders into `document.createElement('video')` inside a
//     `div.fm-video` container, and that container IS what getView() returns.
//   * Audio renders into `document.createElement('audio')` that the SDK holds
//     privately and never puts in the view at all.
//
// So a remote participant's audio plays perfectly while the view contains no
// trace of it — which is exactly the failure we hit: audible audio, an empty
// tile, and an empty transcript.
//
// `_getInternal()._getAudioMediaStream()` / `_getVideoMediaStream()` on
// WebRtcMedia return the native streams directly. They are underscore-prefixed
// and marked @internal, so this file is deliberately the only place that
// touches them, with fallbacks for a build that names them differently.

type Internal = {
  _getAudioMediaStream?: () => unknown;
  _getVideoMediaStream?: () => unknown;
};

type MediaLike = {
  _getInternal?: () => Internal | undefined;
  getView?: () => HTMLElement | null;
  getAudioTrack?: () => unknown;
};

function isMediaStream(value: unknown): value is MediaStream {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as MediaStream).getAudioTracks === 'function'
  );
}

function isMediaStreamTrack(value: unknown): value is MediaStreamTrack {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as MediaStreamTrack).kind === 'string'
  );
}

/** The browser MediaStream carrying this media's audio, if there is one yet. */
export function nativeAudioStream(media: MediaLike): MediaStream | null {
  const stream = media._getInternal?.()?._getAudioMediaStream?.();
  return isMediaStream(stream) ? stream : null;
}

/** The browser MediaStream carrying this media's video, if there is one yet. */
export function nativeVideoStream(media: MediaLike): MediaStream | null {
  const stream = media._getInternal?.()?._getVideoMediaStream?.();
  return isMediaStream(stream) ? stream : null;
}

/**
 * The audio track to feed the transcription pipeline.
 *
 * Null is a normal answer early on: the stream is not attached until the
 * connection is actually carrying media, which is why callers poll rather than
 * asking once.
 */
export function audioTrackOf(media: MediaLike): MediaStreamTrack | null {
  const stream = nativeAudioStream(media);
  const fromStream = stream?.getAudioTracks()[0];
  if (fromStream) return fromStream;

  // Fallbacks for a build that exposes it differently. getAudioTrack() returns
  // an fm.liveswitch.AudioTrack wrapper, not a browser track, but some builds
  // hand the native object through it.
  const wrapper = media.getAudioTrack?.() as
    | { getMediaStreamTrack?: () => unknown }
    | undefined;
  if (isMediaStreamTrack(wrapper)) return wrapper;
  const unwrapped = wrapper?.getMediaStreamTrack?.();
  return isMediaStreamTrack(unwrapped) ? unwrapped : null;
}
